import { el } from "./dom.js";
import { MAG, magTileBg } from "../utils/magazine.js";

// A shared search-result card used by every Add-* view (games / books / TV / anime).
//
// Behaviour:
//   • Tapping the row EXPANDS it in place to reveal a summary + details — it never
//     silently adds. Add is always an explicit button press.
//   • The title is Anton caps; the Add/Added button toggles membership and
//     repaints in place, so expansion survives the toggle.
//
// opts:
//   cover_url, title, subtitle  — the always-visible row
//   mediaType                   — tints the cover thumb when there's no art
//   summary                     — shown in the expanded panel
//   details: [{ label, value }] — extra rows in the expanded panel (rendered as chips)
//   added                       — initial membership state
//   onToggleAdd(want)           — async; resolve to the new added state; throw on failure.
//   loadDetails()               — optional async; called the FIRST time the row expands
//                                 and resolves { summary, details } to fill the panel
//                                 (for sources whose search rows are thin, e.g. OMDb).
export function expandableResultCard({
	cover_url,
	title,
	subtitle = "",
	mediaType,
	summary = "",
	details = [],
	added = false,
	onToggleAdd,
	loadDetails = null,
	// The action button's two faces (the re-link sheet says "Use this").
	addLabel = "+ Add",
	addedLabel = "✓ Added",
}) {
	let isAdded = added;

	const actionBtn = el("button", { type: "button" });
	function paintBtn() {
		actionBtn.textContent = isAdded ? addedLabel : addLabel;
		actionBtn.setAttribute("aria-pressed", isAdded ? "true" : "false");
		Object.assign(actionBtn.style, {
			fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "10px",
			letterSpacing: ".06em", textTransform: "uppercase", borderRadius: "999px",
			padding: "8px 14px", cursor: "pointer", flex: "none", lineHeight: "1",
			color: isAdded ? MAG.lime : "#0b0b0f",
			background: isAdded ? "transparent" : MAG.lime,
			border: isAdded ? `1.5px solid ${MAG.lime}` : "0",
		});
	}

	actionBtn.onclick = async (e) => {
		e.stopPropagation();
		actionBtn.disabled = true;
		try {
			const result = await onToggleAdd(!isAdded);
			isAdded = result === undefined ? !isAdded : !!result;
			paintBtn();
			repaintCard();
		} catch {
			// The view surfaces the error in its status line.
		} finally {
			actionBtn.disabled = false;
		}
	};

	const cover = el("div", {
		style: {
			width: "46px", height: "62px", flex: "none", borderRadius: "8px",
			boxShadow: "0 2px 8px rgba(0,0,0,.4)", background: magTileBg({ cover_url, media_type: mediaType }),
		},
	});

	const chevron = el("span", { style: { color: MAG.dim, fontSize: "13px", flex: "none" } }, "▾");

	const panel = el("div", { hidden: true, style: { padding: "0 13px 13px" } });
	function fillPanel(text, rows, { placeholder = "No description available." } = {}) {
		while (panel.firstChild) panel.removeChild(panel.firstChild);
		if (text) {
			panel.append(el("p", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "500", fontSize: "12.5px",
					lineHeight: "1.5", color: "#cfcfd6", margin: "0 0 11px", textWrap: "pretty",
				},
			}, text));
		}
		const chips = el("div", { style: { display: "flex", gap: "7px", flexWrap: "wrap" } });
		for (const d of rows ?? []) {
			if (d == null || d.value == null || d.value === "") continue;
			chips.append(el("span", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9px",
					letterSpacing: ".05em", textTransform: "uppercase", color: "#19d3e8",
					background: "rgba(25,211,232,.12)", border: "1px solid rgba(25,211,232,.35)",
					borderRadius: "6px", padding: "4px 8px",
				},
			}, d.value));
		}
		if (chips.children.length) panel.append(chips);
		if (!text && !chips.children.length) {
			panel.append(el("p", { style: { fontFamily: "'Archivo', sans-serif", fontSize: "12.5px", color: MAG.dim, margin: "0" } }, placeholder));
		}
	}
	fillPanel(summary, details);

	// Thin search rows (OMDb) load their summary/details the first time they
	// expand; a failed load leaves the row expandable again.
	let detailsLoaded = !loadDetails;
	async function ensureDetails() {
		if (detailsLoaded) return;
		detailsLoaded = true;
		fillPanel("", [], { placeholder: "Loading…" });
		try {
			const more = await loadDetails();
			fillPanel(more?.summary ?? summary, more?.details ?? details);
		} catch {
			detailsLoaded = false;
			fillPanel(summary, details, { placeholder: "Couldn't load details — tap again to retry." });
		}
	}

	let expanded = false;
	function toggleExpand() {
		expanded = !expanded;
		panel.hidden = !expanded;
		if (expanded) ensureDetails();
	}

	const row = el("div", {
		role: "button",
		tabindex: "0",
		onclick: toggleExpand,
		onkeydown: (e) => {
			if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleExpand(); }
		},
		style: { display: "flex", gap: "12px", alignItems: "center", padding: "12px", cursor: "pointer" },
	},
		cover,
		el("div", { style: { flex: "1", minWidth: "0" } },
			el("div", {
				style: { fontFamily: "'Anton', sans-serif", fontSize: "20px", lineHeight: ".95", textTransform: "uppercase", color: "#fff" },
			}, title),
			subtitle
				? el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9.5px",
						letterSpacing: ".06em", textTransform: "uppercase", color: MAG.dim, marginTop: "3px",
					},
				}, subtitle)
				: "",
		),
		chevron,
		actionBtn,
	);

	const card = el("div", {});
	function repaintCard() {
		Object.assign(card.style, {
			borderRadius: "15px", overflow: "hidden",
			background: isAdded ? "rgba(255,255,255,.07)" : "rgba(255,255,255,.06)",
			border: isAdded ? `1.5px solid ${MAG.lime}` : `1px solid ${MAG.line}`,
			backdropFilter: "blur(14px)", webkitBackdropFilter: "blur(14px)",
		});
	}
	card.append(row, panel);
	paintBtn();
	repaintCard();
	return card;
}
